import { format, parseISO } from 'date-fns';

import {
  CREW_ASSIGNMENTS,
  JOB_INVOICES,
  PROJECT_JOBS,
  PROJECT_ORDERS,
  STATUS_HISTORY,
  CrewAssignment,
  JobInvoice,
  ProjectJob,
  ProjectOrder,
  StatusChange,
} from '../data/projectsData';

// Real "today", used to anchor the calendar and scheduling views to the current week.
export const TODAY = format(new Date(), 'yyyy-MM-dd');

// Friendly labels + colors for the three ServMan project departments in the dataset.
export const DEPARTMENT_META: Record<string, { label: string; color: string }> = {
  TMSCSPRJ: { label: 'Projects', color: '#1565c0' },
  TMSCSCON: { label: 'Controls', color: '#2e7d32' },
  TMSCSCPM: { label: 'Construction PM', color: '#c62828' },
};

export const departmentLabel = (code: string): string => DEPARTMENT_META[code]?.label || code;
export const departmentColor = (code: string): string => DEPARTMENT_META[code]?.color || '#546e7a';

export const WORK_TYPE_LABELS: Record<string, string> = {
  'PROJ-SCOPE': 'Scoping',
  'PROJ-INSTA': 'Installation',
  'PROJ-START': 'Startup',
};

export const workTypeLabel = (workType: string): string => WORK_TYPE_LABELS[workType] || workType || 'Work';

// Deterministic per-job color so a job keeps the same swatch everywhere on the calendar.
const JOB_PALETTE = [
  '#1565c0',
  '#6a1b9a',
  '#2e7d32',
  '#c62828',
  '#e65100',
  '#00695c',
  '#4527a0',
  '#ad1457',
  '#283593',
  '#558b2f',
  '#d84315',
  '#00838f',
  '#5d4037',
  '#827717',
  '#0277bd',
  '#7b1fa2',
];

export const colorForJob = (jobId: string): string => {
  let hash = 0;
  for (let i = 0; i < jobId.length; i += 1) {
    hash = (hash * 31 + jobId.charCodeAt(i)) % 2147483647;
  }
  return JOB_PALETTE[hash % JOB_PALETTE.length];
};

// Phase colors shared by WorkTypeChip and the scheduling-group card headers.
export const PHASE_HEADER_COLORS: Record<string, string> = {
  'PROJ-SCOPE': '#9e9d24',
  'PROJ-INSTA': '#0277bd',
  'PROJ-START': '#7b1fa2',
};

export const formatCurrency = (amount: number): string =>
  amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const formatDateDisplay = (isoDate: string | null): string => {
  if (!isoDate) return '—';
  const date = parseISO(isoDate);
  // ServMan uses 1/1/1900 (and 12/31/1899) as the universal "no date" sentinel — show as blank.
  if (Number.isNaN(date.getTime()) || date.getFullYear() <= 1900) return '—';
  return format(date, 'MM/dd/yyyy');
};

export const jobById = (jobId: string): ProjectJob | undefined => PROJECT_JOBS.find((job) => job.job_id === jobId);

export const jobsByDepartment = (deptCode: string): ProjectJob[] =>
  PROJECT_JOBS.filter((job) => job.dept_id === deptCode);

export const ordersForJob = (jobId: string): ProjectOrder[] => PROJECT_ORDERS.filter((order) => order.job_id === jobId);

export const orderByNo = (orderNo: string): ProjectOrder | undefined =>
  PROJECT_ORDERS.find((order) => order.order_no === orderNo);

export const jobForOrder = (orderNo: string): ProjectJob | undefined => {
  const order = orderByNo(orderNo);
  return order ? jobById(order.job_id) : undefined;
};

export const assignmentsForOrder = (orderNo: string): CrewAssignment[] =>
  CREW_ASSIGNMENTS.filter((assignment) => assignment.order_no === orderNo);

export const assignmentsForJob = (jobId: string): CrewAssignment[] =>
  CREW_ASSIGNMENTS.filter((assignment) => assignment.job_id === jobId);

export const assignmentsForDate = (isoDate: string): CrewAssignment[] =>
  CREW_ASSIGNMENTS.filter((assignment) => assignment.date === isoDate);

export const statusHistoryForOrder = (orderNo: string): StatusChange[] =>
  STATUS_HISTORY.filter((change) => change.order_no === orderNo);

export const scheduledHours = (assignments: CrewAssignment[]): number =>
  assignments.reduce((total, assignment) => total + assignment.duration, 0);

export const scheduledDateRange = (assignments: CrewAssignment[]): { first: string | null; last: string | null } => {
  const dates = assignments
    .map((assignment) => assignment.date)
    .filter(Boolean)
    .sort();
  return { first: dates[0] || null, last: dates[dates.length - 1] || null };
};

// Contact/customer fields now live on the job itself (CLIENT join); kept as a helper
// so the detail page reads a stable shape.
export const contactForJob = (jobId: string): ProjectJob | undefined => jobById(jobId);

export const invoicesForJob = (jobId: string): JobInvoice[] =>
  JOB_INVOICES.filter((invoice) => invoice.job_id === jobId);

export const invoicedTotalForJob = (jobId: string): number =>
  invoicesForJob(jobId).reduce((total, invoice) => total + invoice.amount, 0);
